import { graphqlOperation } from "aws-amplify";
import {
  conversationsByOwnerLastMessage,
  conversationsByOwnerReadState,
  getConversation,
  conversationMessagesByConversation,
  conversationSyncStatesByOwner,
  listMessageAccounts,
  sendConversationReply,
  markConversationRead,
  translateConversation as translateConversationMutation,
  startOrderConversation as startOrderConversationMutation,
  conversationItemOrderLines,
  conversationItemListing,
  conversationItemBySku,
  ITEM_MATCH_FIELDS,
  ITEM_INVENTORY_FIELDS,
} from "@/graphql/buyerMessages";
import { buildBatch, chunk } from "@/utils/omsGraphqlBatch";
import { locationBadges, itemResolutionKey, fallbackItemRow, itemRowFrom, resolveSkuRows, rowLocation, firstImage } from "@/utils/buyerMessages";

// Every function takes `api` (Amplify's API) so it tests without the network,
// the way cancellationsQueue.js does. All reads are newest-first.

const PAGE = 50;
const UNREAD = { beginsWith: { readState: "unread" } };

/**
 * The inbox lists conversations with a message in the last INBOX_DAYS days. Older ones stay
 * only while unread or while Ivy has them waiting (keptOlder); nothing is deleted, and a
 * direct link still opens any conversation.
 */
export const INBOX_DAYS = 15;

/** The ISO time before which a conversation drops out of the inbox. */
export function inboxCutoff(now = Date.now()) {
  return new Date(now - INBOX_DAYS * 86400000).toISOString();
}

/**
 * One page of conversations. Returns { items, nextToken }. `since` (ISO) limits the
 * all-conversations list to lastMessageAt >= since by key condition, so paging stops there.
 * The unread list ignores it: an unread conversation of any age stays.
 */
export async function listConversations(api, owner, { unreadOnly = false, nextToken = null, since = null } = {}) {
  const res = unreadOnly
    ? await api.graphql(
        graphqlOperation(conversationsByOwnerReadState, {
          owner, readStateLastMessageAt: UNREAD, sortDirection: "DESC", limit: PAGE, nextToken,
        })
      )
    : await api.graphql(
        graphqlOperation(conversationsByOwnerLastMessage, {
          owner, ...(since ? { lastMessageAt: { ge: since } } : {}), sortDirection: "DESC", limit: PAGE, nextToken,
        })
      );
  const page = res.data[unreadOnly ? "conversationsByOwnerReadState" : "conversationsByOwnerLastMessage"];
  return { items: (page.items || []).filter(Boolean), nextToken: page.nextToken || null };
}

/** Every unread conversation (the badge count and the Order Queue map). */
export async function listUnreadConversations(api, owner) {
  const out = [];
  let nextToken = null;
  do {
    const page = await listConversations(api, owner, { unreadOnly: true, nextToken });
    out.push(...page.items);
    nextToken = page.nextToken;
  } while (nextToken);
  return out;
}

export async function fetchConversation(api, id) {
  const res = await api.graphql(graphqlOperation(getConversation, { id }));
  return res.data.getConversation || null;
}

/** `fn` over `list` with at most `limit` calls in flight; results in `list`'s order. A rejection rejects the whole call. */
export async function mapLimit(list, limit, fn) {
  const out = new Array(list.length);
  let next = 0;
  const worker = async () => {
    while (next < list.length) {
      const i = next++;
      out[i] = await fn(list[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, list.length)) }, worker));
  return out;
}

// Ivy can hold hundreds of open conversations: they are read this many at a time.
export const FETCH_CONCURRENCY = 8;

/**
 * The conversations older than `cutoff` the inbox keeps: unread ones (from the complete unread
 * set the layout already loads) and ones Ivy has open items on (read FETCH_CONCURRENCY at a time).
 * `have` holds ids already listed. A failed read leaves that row out. Newest first.
 */
export async function keptOlder(api, { cutoff, unread = [], ivyConversationIds = [], have = new Set() }) {
  const old = (c) => c && c.lastMessageAt && c.lastMessageAt < cutoff;
  const out = new Map();
  for (const c of unread) if (old(c) && !have.has(c.id)) out.set(c.id, c);
  const fetch = [...new Set(ivyConversationIds)].filter((id) => id && !have.has(id) && !out.has(id));
  const rows = await mapLimit(fetch, FETCH_CONCURRENCY, (id) => fetchConversation(api, id).catch(() => null));
  for (const c of rows) if (old(c)) out.set(c.id, c);
  return [...out.values()].sort((a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt));
}

/** The whole thread, oldest first. */
export async function getThread(api, conversationId) {
  const out = [];
  let nextToken = null;
  do {
    const res = await api.graphql(
      graphqlOperation(conversationMessagesByConversation, { conversationId, sortDirection: "ASC", limit: 200, nextToken })
    );
    const page = res.data.conversationMessagesByConversation;
    out.push(...(page.items || []).filter(Boolean));
    nextToken = page.nextToken || null;
  } while (nextToken);
  return out;
}

/** { accountId: { id, username, type } } for the account label on each row. */
export async function listAccountsById(api) {
  const byId = {};
  let nextToken = null;
  do {
    const res = await api.graphql(graphqlOperation(listMessageAccounts, { limit: 200, nextToken }));
    const page = res.data.listMarketplaceAccounts;
    (page.items || []).filter(Boolean).forEach((a) => { byId[a.id] = a; });
    nextToken = page.nextToken || null;
  } while (nextToken);
  return byId;
}

export async function listSyncStates(api, owner) {
  const res = await api.graphql(graphqlOperation(conversationSyncStatesByOwner, { owner, limit: 200 }));
  return (res.data.conversationSyncStatesByOwner.items || []).filter(Boolean);
}

/**
 * Send a reply through the conversation's marketplace. `seenInboundAt` is the lastInboundAt of the thread
 * as the staff member saw it, so a message that arrived meanwhile stays unread. `clientMessageId` is the
 * idempotency key of this composed reply (Depop deduplicates on it; the other marketplaces ignore it).
 */
export async function sendReply(api, conversationId, text, seenInboundAt, clientMessageId) {
  const variables = { conversationId, text, seenInboundAt: seenInboundAt || null };
  if (clientMessageId) variables.clientMessageId = clientMessageId;
  const res = await api.graphql(graphqlOperation(sendConversationReply, variables));
  return res.data.sendConversationReply;
}

/**
 * Send the first (or next) message on a Poshmark order from the Order Queue.
 * Resolves { conversationId, confirmed }: confirmed = Sellary read the message
 * back from Poshmark and the conversation exists.
 */
export async function startOrderConversation(api, orderId, text) {
  const res = await api.graphql(graphqlOperation(startOrderConversationMutation, { orderId, text }));
  return res.data.startOrderConversation;
}

/**
 * OPT-461: translate this conversation's untranslated inbound messages into the seller's language.
 * -> [{ id, sourceLanguage, translatedLanguage, translatedBody }] for the whole thread; merge by id.
 * Errors carry errorCode(): NO_CREDITS, TRANSLATION_BUSY, TRANSLATE_FAILED, NOT_AVAILABLE, FEATURE_REQUIRED.
 */
export async function translateConversation(api, conversationId) {
  const res = await api.graphql(graphqlOperation(translateConversationMutation, { conversationId }));
  return ((res.data && res.data.translateConversation) || []).filter(Boolean);
}

export async function markRead(api, conversationId, seenInboundAt) {
  const res = await api.graphql(graphqlOperation(markConversationRead, { conversationId, seenInboundAt: seenInboundAt || null }));
  return res.data.markConversationRead;
}

/**
 * The code of a failed mutation: EBAY_REJECTED, POSHMARK_REJECTED,
 * DELIVERY_UNKNOWN, RECONNECT_REQUIRED, RELOGIN_REQUIRED, REPLIES_CLOSED,
 * DEPOP_REJECTED, SESSION_EXPIRED, REPLIES_UNAVAILABLE, PHONE_VERIFICATION_REQUIRED,
 * BAD_REQUEST, NOT_FOUND. AppSync puts the Lambda's error
 * name in errorType and our own "CODE: message" in the message; read either.
 */
export function errorCode(e) {
  const first = e && Array.isArray(e.errors) && e.errors[0];
  const fromType = first && first.errorType;
  const text = (first && first.message) || (e && e.message) || "";
  const m = /^([A-Z_]+):/.exec(text);
  const code = (m && m[1]) || (fromType && /^[A-Z_]+$/.test(fromType) ? fromType : null);
  return { code, message: text.replace(/^[A-Z_]+:\s*/, "") || "Something went wrong." };
}

// ---- OPT-81: the item a conversation is about -------------------------------

async function allPages(api, query, variables, field) {
  const out = [];
  let nextToken = null;
  do {
    const res = await api.graphql(graphqlOperation(query, { ...variables, limit: 100, nextToken }));
    const page = res.data[field];
    out.push(...((page && page.items) || []).filter(Boolean));
    nextToken = (page && page.nextToken) || null;
  } while (nextToken);
  return out;
}

/** -> { item, found } for a SKU, or throws. See resolveSkuRows for the rules. */
async function lookupSku(api, owner, sku) {
  const rows = await allPages(api, conversationItemBySku, { owner, sku: { eq: String(sku).trim() } }, "lookupByOwnerAndSKU");
  return resolveSkuRows(rows);
}

/** One row from a SKU lookup result; a lookup that threw degrades to unfound. */
function skuRow(line, lookup, c) {
  if (!lookup || !lookup.found) return itemRowFrom(line, null, c, { found: false });
  return itemRowFrom(line, lookup.item, c, { found: true, candidates: lookup.candidates });
}

/**
 * `batched` (utils/omsGraphqlBatch) rejects the whole fan-out when one request
 * fails. Here each 25-alias request stands alone: a failed one costs only its
 * own ids, and a partial GraphQL error (Amplify rejects with `{ data, errors }`)
 * keeps the aliases that did resolve. -> { id: result }
 */
async function batchedPartial(api, opName, field, argName, argType, ids, selection, onError) {
  const out = {};
  await Promise.all(
    chunk([...new Set(ids)], 25).map(async (group, gi) => {
      const { query, variables, aliases } = buildBatch(opName + gi, field, argName, argType, group, selection);
      let data = null;
      try {
        data = (await api.graphql(graphqlOperation(query, variables))).data;
      } catch (e) {
        onError(opName, e);
        data = (e && e.data) || null;
      }
      if (!data) return;
      aliases.forEach(([alias, id]) => {
        if (data[alias]) out[id] = data[alias];
      });
    })
  );
  return out;
}

async function orderRows(api, c, onError) {
  const lines = (await allPages(api, conversationItemOrderLines, { orderId: c.orderId }, "orderLinesByOrder")).filter(
    (l) => !l.removed
  );
  if (!lines.length) return null;

  const matches = await batchedPartial(api, "ItemMatches", "orderMatchesByLine", "orderLineId", "ID", lines.map((l) => l.id), {
    connection: true,
    body: `items { ${ITEM_MATCH_FIELDS} } nextToken`,
  }, onError);
  const invIdFor = (l) => {
    const m = ((matches[l.id] && matches[l.id].items) || []).find((x) => x && x.state === "matched" && x.inventoryId);
    return m ? m.inventoryId : null;
  };
  const invIds = [...new Set(lines.map(invIdFor).filter(Boolean))];
  const invs = invIds.length
    ? await batchedPartial(api, "ItemInventory", "getInventory", "id", "ID!", invIds, { connection: false, body: ITEM_INVENTORY_FIELDS }, onError)
    : {};

  // Lines with no live inventory item (unmatched, or the matched item is gone) try their SKU, once per SKU.
  const inv = (l) => (invIdFor(l) && invs[invIdFor(l)]) || null;
  const skus = [...new Set(lines.filter((l) => !inv(l) && l.sku && String(l.sku).trim()).map((l) => String(l.sku).trim()))];
  const lookups = {};
  await Promise.all(
    skus.map(async (sku) => {
      try {
        lookups[sku] = await lookupSku(api, c.owner, sku);
      } catch (e) {
        onError("sku", e);
      }
    })
  );
  return lines.map((l) => {
    const i = inv(l);
    if (i) return itemRowFrom(l, i, c);
    return skuRow(l, l.sku ? lookups[String(l.sku).trim()] : null, c);
  });
}

async function listingRow(api, c, onError) {
  let inv = null;
  if (c.listingId) {
    try {
      const res = await api.graphql(graphqlOperation(conversationItemListing, { id: c.listingId }));
      inv = (res.data.getListing && res.data.getListing.inventory) || null;
    } catch (e) {
      onError("listing", e);
    }
  }
  if (inv) {
    const sku = inv.sku || String(c.listingSku || "").trim() || null;
    return {
      key: inv.id || "listing", title: inv.title || fallbackItemRow(c).title, sku,
      image: firstImage(inv), quantity: null, found: true,
      ...rowLocation({ found: true, item: inv, sku }),
    };
  }
  if (c.listingSku) {
    try {
      const { item, found, candidates } = await lookupSku(api, c.owner, c.listingSku);
      if (found) {
        const sku = (item && item.sku) || String(c.listingSku).trim();
        return {
          key: "listing", title: (item && item.title) || fallbackItemRow(c).title, sku,
          image: firstImage(item), quantity: null, found: true,
          ...rowLocation({ found, item, candidates, sku }),
        };
      }
    } catch (e) {
      onError("sku", e);
    }
  }
  return null;
}

/**
 * The items a conversation is about, for the card at the top of its thread.
 * Rows: { key, title, sku, image, quantity, found, location, locationSource }. `found` = an inventory
 * item exists, which is what lets the SKU link. A failed read degrades only
 * the rows that needed it; this never throws. `onError(step, e)` is told.
 */
export async function fetchConversationItems(api, c, onError = () => {}) {
  if (c.orderId) {
    try {
      const rows = await orderRows(api, c, onError);
      if (rows) return rows;
    } catch (e) {
      onError("orderLines", e);
    }
  }
  const one = await listingRow(api, c, onError);
  return [one || fallbackItemRow(c)];
}

/**
 * OPT-294: badges for each conversation, resolved with fetchConversationItems so the row agrees with
 * the thread's item card. At most `concurrency` conversations resolve at once (each may fan out a few
 * calls of its own). -> { [conversationId]: { key, badges, failed } }; failed = a read failed, so
 * an empty `badges` may be wrong and should be retried.
 */
export async function resolveConversationBadges(api, conversations, { setup, concurrency = 4, onError } = {}) {
  const out = {};
  let next = 0;
  const worker = async () => {
    while (next < conversations.length) {
      const c = conversations[next++];
      let failed = false;
      const rows = await fetchConversationItems(api, c, (step, e) => {
        failed = true;
        if (onError) onError(step, e);
      });
      out[c.id] = { key: itemResolutionKey(c), badges: locationBadges(rows, setup), failed };
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, conversations.length)) }, worker));
  return out;
}
